/**
 * telebirr SuperApp bridge helpers.
 *
 * Supports three host environments:
 * 1. H5 URL in SuperApp  → window.consumerapp.evaluate(...)
 * 2. MiniApp H5 + js-sdk → window.ma.native(method, params)
 * 3. Native Macle pages  → window.ma.getMiniAppToken / window.ma.startPay
 */

import {
  debugLog,
  debugWarn,
  debugError,
  debugTimer,
  describeError,
  maskToken,
} from "./debug";

const SCOPE = "telebirr";

/**
 * H5 token bridge, verified against the SuperApp build (SuperAppTestBed.apk):
 *
 *   if/h.G()  -> "js_fun_h5GetAccessToken"
 *   if/h.C(params) -> params.optString("appid")   <- lowercase "appid"
 *                  -> H5RequestPinRepository -> POST v1/h5GetAccessToken
 *   on success: hf/d.g(cb, token) -> javascript:<cb>('<raw token string>')
 *   on failure: a native toast with the server's message, and NO js callback
 *
 * The lowercase key is what matters: optString("appid") on a payload carrying
 * "appId" returns "", the SuperApp posts an empty appId, and the server answers
 * with a parameter validation error that the app toasts and never calls back.
 * We send both spellings so the call survives either casing.
 */
const H5_TOKEN_FUNCTION =
  (process.env.REACT_APP_TELEBIRR_H5_TOKEN_FN || "").trim() ||
  "js_fun_h5GetAccessToken";

const CALLBACK_TIMEOUT_MS = 20000;

/** Reject if a bridge promise never settles, so login cannot spin forever. */
const withTimeout = (promise, label) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`telebirr did not answer ${label}`));
    }, CALLBACK_TIMEOUT_MS);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

/** Which host bridges are actually present — the first thing to check when login fails. */
export const describeBridgeEnv = () => ({
  "ma": typeof window.ma,
  "ma.native": typeof (window.ma && window.ma.native),
  "ma.getMiniAppToken": typeof (window.ma && window.ma.getMiniAppToken),
  "ma.startPay": typeof (window.ma && window.ma.startPay),
  "consumerapp": typeof window.consumerapp,
  "consumerapp.evaluate": typeof (window.consumerapp && window.consumerapp.evaluate),
  userAgent: navigator.userAgent,
});

const makeCallbackName = (prefix) =>
  `__telebirr_${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const isEmptyPayload = (args) =>
  args.length === 0 ||
  args.every((arg) => arg === undefined || arg === null || arg === "");

/**
 * H5 SuperApp docs: consumerapp.evaluate({ functionName, params.functionCallBackName }).
 *
 * Checkout's sample always uses window.handleinitDataCallback and hands back no
 * payment result payload — the sample just reloads the page. So we listen on our
 * own unique callback name *and* on handleinitDataCallback, but an empty
 * handleinitDataCallback only settles a call that expects no payload
 * (`allowEmpty`). For the token call it must not: resolving on an empty stray
 * callback is exactly how an undefined token gets through.
 */
const callConsumerApp = (functionName, params, options = {}) => {
  const {
    callbackName,
    allowEmpty = false,
    timeoutMs = CALLBACK_TIMEOUT_MS,
  } = options;

  return new Promise((resolve, reject) => {
    if (!window.consumerapp || typeof window.consumerapp.evaluate !== "function") {
      debugError(SCOPE, `consumerapp.evaluate missing for ${functionName}`, describeBridgeEnv());
      reject(new Error("consumerapp unavailable"));
      return;
    }

    const name = callbackName || makeCallbackName(functionName);
    const aliases = Array.from(new Set([name, "handleinitDataCallback"]));
    const previous = {};
    let settled = false;
    const startedAt = Date.now();

    const cleanup = () => {
      clearTimeout(watchdog);
      aliases.forEach((alias) => {
        if (previous[alias] === undefined) delete window[alias];
        else window[alias] = previous[alias];
      });
    };

    const finish = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    const fail = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    // The host calls back into window[...]; if it never does, login just hangs.
    // Reject instead of leaving a spinner with no explanation.
    const watchdog = setTimeout(() => {
      debugError(
        SCOPE,
        `${functionName}: no usable callback after ${timeoutMs}ms`,
        { listeningOn: aliases, env: describeBridgeEnv() }
      );
      fail(new Error(`telebirr did not answer ${functionName}`));
    }, timeoutMs);

    aliases.forEach((alias) => {
      previous[alias] = window[alias];
      // Log every argument: hosts differ on whether they pass a string, an
      // object, or (code, data). Seeing the real shape beats guessing.
      window[alias] = (...args) => {
        const empty = isEmptyPayload(args);
        debugLog(SCOPE, `${functionName}: ${alias} fired after ${Date.now() - startedAt}ms`, {
          argCount: args.length,
          args: args.map((arg) =>
            typeof arg === "string" ? maskToken(arg) : arg
          ),
        });

        if (empty && !allowEmpty) {
          // A bare handleinitDataCallback() carries no token. Keep waiting for
          // the real one rather than resolving with undefined.
          debugWarn(
            SCOPE,
            `${functionName}: ignoring empty ${alias} callback (no payload to read a token from)`
          );
          return;
        }

        finish(args.length > 1 ? args : args[0]);
      };
    });

    try {
      const payload = {
        functionName,
        params: {
          ...params,
          functionCallBackName: name,
        },
      };
      debugLog(SCOPE, `${functionName}: consumerapp.evaluate`, {
        listeningOn: aliases,
        payload,
      });
      window.consumerapp.evaluate(JSON.stringify(payload));
    } catch (error) {
      debugError(SCOPE, `${functionName}: evaluate threw`, describeError(error));
      fail(error);
    }
  });
};

const asObject = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.startsWith("{")) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  return null;
};

/**
 * When the current H5 URL is not on the SuperApp's h5InterfacePermission list,
 * com.huawei.webview.b#b calls our callback with
 *   {"resultCode":"1","result":"fail","message":"No permission"}
 * Note resultCode "1" — the same code checkout uses for success — so this has
 * to be recognised by "result": "fail" before any success check runs.
 */
export const bridgeFailureMessage = (value) => {
  const obj = asObject(value);
  if (!obj) return null;
  const result = typeof obj.result === "string" ? obj.result.toLowerCase() : "";
  if (result === "fail" || result === "failed") {
    return obj.message || obj.msg || "telebirr rejected the request";
  }
  return null;
};

/**
 * Hosts answer with a bare string, an object, or a JSON string, and nest the
 * token under a different key each time. Dig for it instead of assuming.
 */
const normalizeToken = (value) => {
  if (bridgeFailureMessage(value)) return null;
  if (!value) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return normalizeToken(JSON.parse(trimmed));
      } catch (_) {
        /* not JSON — treat as the raw token */
      }
    }
    return trimmed;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = normalizeToken(item);
      if (found) return found;
    }
    return null;
  }

  if (typeof value === "object") {
    const direct =
      value.token ||
      value.access_token ||
      value.accessToken ||
      value.miniAppToken;
    if (typeof direct === "string" && direct.trim()) return direct.trim();

    for (const key of ["data", "result", "biz_content", "bizContent", "params"]) {
      // Only recurse into containers: a bare string under .result is a status
      // word ("fail"), not a token.
      if (value[key] && typeof value[key] === "object") {
        const found = normalizeToken(value[key]);
        if (found) return found;
      }
    }
  }

  return null;
};

export const isInTelebirrSuperApp = () => {
  const ma = window.ma;
  if (
    ma &&
    (typeof ma.native === "function" ||
      typeof ma.getMiniAppToken === "function" ||
      typeof ma.startPay === "function")
  ) {
    debugLog(SCOPE, "host detected: ma (MiniApp/Macle)");
    return true;
  }

  const h5 =
    window.consumerapp && typeof window.consumerapp.evaluate === "function";
  debugLog(
    SCOPE,
    h5 ? "host detected: consumerapp (H5)" : "no telebirr host detected",
    describeBridgeEnv()
  );
  return Boolean(h5);
};

/**
 * Request a SuperApp access / mini-app token for the given merchant appId.
 * Resolves to a string token.
 */
export const getMiniAppToken = (appId) => {
  if (!appId) {
    debugError(SCOPE, "getMiniAppToken called without an appId");
    return Promise.reject(new Error("Missing telebirr appId"));
  }

  debugLog(SCOPE, "getMiniAppToken requested", { appId, env: describeBridgeEnv() });

  // MiniApp H5 JS SDK (js-sdk.min.js)
  if (window.ma && typeof window.ma.native === "function") {
    const done = debugTimer(SCOPE, "getMiniAppToken via ma.native");
    try {
      const call = window.ma.native("getMiniAppToken", { appId });
      // Not every js-sdk build returns a promise here; fall through to the
      // callback API rather than blowing up on `undefined.then`.
      if (call && typeof call.then === "function") {
        return withTimeout(call, "getMiniAppToken")
          .then((tokenResp) => {
            const token = normalizeToken(tokenResp);
            if (!token) {
              done("empty", { raw: tokenResp });
              throw new Error("Empty telebirr token");
            }
            done("ok", { token: maskToken(token) });
            return token;
          })
          .catch((error) => {
            debugError(SCOPE, "ma.native getMiniAppToken failed", describeError(error));
            throw error;
          });
      }
      done("not a promise", { returned: typeof call });
    } catch (error) {
      debugError(SCOPE, "ma.native getMiniAppToken threw", describeError(error));
    }
  }

  // Native Macle mini-app API — the shape proven by the Macle build:
  //   ma.getMiniAppToken({ appId, success: (res) => res.token })
  if (window.ma && typeof window.ma.getMiniAppToken === "function") {
    const done = debugTimer(SCOPE, "getMiniAppToken via ma.getMiniAppToken");
    return new Promise((resolve, reject) => {
      let settled = false;
      let timer;
      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        fn(value);
      };
      timer = setTimeout(() => {
        done("timeout");
        finish(reject, new Error("telebirr did not answer getMiniAppToken"));
      }, CALLBACK_TIMEOUT_MS);

      window.ma.getMiniAppToken({
        appId,
        success: (res) => {
          const token = normalizeToken(res);
          if (!token) {
            done("empty", { raw: res });
            finish(reject, new Error("Empty telebirr token"));
          } else {
            done("ok", { token: maskToken(token) });
            finish(resolve, token);
          }
        },
        fail: (err) => {
          done("fail", describeError(err));
          finish(reject, err || new Error("getMiniAppToken failed"));
        },
      });
    });
  }

  // Pure H5 SuperApp bridge (registered H5 URL)
  if (window.consumerapp && typeof window.consumerapp.evaluate === "function") {
    const done = debugTimer(SCOPE, `getMiniAppToken via consumerapp ${H5_TOKEN_FUNCTION}`);
    // "appid" is the key the SuperApp reads; "appId" is sent alongside it in
    // case a build ever changes its mind about the casing.
    return callConsumerApp(H5_TOKEN_FUNCTION, { appid: appId, appId }).then(
      (accessToken) => {
        const rejected = bridgeFailureMessage(accessToken);
        if (rejected) {
          done("rejected", { message: rejected, raw: accessToken });
          throw new Error(`telebirr refused the token request: ${rejected}`);
        }
        const token = normalizeToken(accessToken);
        if (!token) {
          done("empty", { raw: accessToken, rawType: typeof accessToken });
          throw new Error("Empty telebirr token");
        }
        done("ok", { token: maskToken(token) });
        return token;
      },
      (error) => {
        done("fail", describeError(error));
        throw error;
      }
    );
  }

  debugError(SCOPE, "no telebirr bridge available for getMiniAppToken", describeBridgeEnv());
  return Promise.reject(new Error("Not opened inside telebirr SuperApp"));
};

/**
 * Hand a create-order rawRequest string to the SuperApp payment UI.
 */
export const startPay = (rawRequest) => {
  const trimmed = (rawRequest || "").trim();
  if (!trimmed) {
    return Promise.reject(new Error("Invalid payment request"));
  }

  if (window.ma && typeof window.ma.native === "function") {
    return window.ma.native("startPay", {
      rawRequest: trimmed,
      bussinessType: "BuyGoods",
    });
  }

  if (window.ma && typeof window.ma.startPay === "function") {
    return new Promise((resolve, reject) => {
      window.ma.startPay({
        rawRequest: trimmed,
        success: resolve,
        fail: (err) => reject(err || new Error("startPay failed")),
      });
    });
  }

  if (window.consumerapp && typeof window.consumerapp.evaluate === "function") {
    // Official H5 Checkout docs:
    //   functionName: "js_fun_start_pay"
    //   functionCallBackName: "handleinitDataCallback"
    //   window.handleinitDataCallback = function () { /* no args */ }
    debugLog(SCOPE, "startPay via consumerapp js_fun_start_pay");
    return callConsumerApp(
      "js_fun_start_pay",
      { rawRequest: trimmed },
      {
        callbackName: "handleinitDataCallback",
        // The pay callback legitimately carries no payload.
        allowEmpty: true,
        // The customer types a PIN in the pay sheet — give them longer than
        // a bridge round trip.
        timeoutMs: 5 * 60 * 1000,
      }
    ).then((result) => {
      debugLog(SCOPE, "H5 pay callback payload", result);
      return { channel: "h5", result };
    });
  }

  return Promise.reject(new Error("Not opened inside telebirr SuperApp"));
};

const unwrapPayResult = (value) => {
  if (value && typeof value === "object" && value.channel === "h5") {
    return value.result;
  }
  return value;
};

const parsePayResult = (result) => {
  if (result == null || result === "") return null;
  if (typeof result === "number") return { resultCode: String(result) };
  if (typeof result === "string") {
    try {
      const parsed = JSON.parse(result);
      if (parsed && typeof parsed === "object") return parsed;
    } catch (_) {
      /* plain code string */
    }
    return { resultCode: result };
  }
  if (typeof result === "object") return result;
  return null;
};

const payCode = (result) => {
  const r = parsePayResult(result);
  if (!r) return "";
  return String(
    r.resultCode ?? r.resultStatus ?? r.code ?? r.status ?? r.trade_status ?? ""
  );
};

/**
 * Closing the pay sheet, verified against the SuperApp build
 * (StartPayEnterActivity.R0), reaches both the H5 callback and Macle's fail() as
 *   {"result":"fail","resultCode":"-1","message":"cancel","errorCode":"2"}
 * Only the message marks a cancel: a sheet timeout carries the same resultCode
 * with message "timeout", and there the money may have moved.
 */
export const isPayCancelled = (result) => {
  const unwrapped = unwrapPayResult(result);
  const parsed = parsePayResult(unwrapped);
  if (parsed && String(parsed.message).toLowerCase() === "cancel") return true;

  const code = payCode(unwrapped).toUpperCase();
  return ["4", "6001", "USER_CANCEL", "CANCELED", "CANCELLED"].includes(code);
};

/**
 * H5 Checkout docs: handleinitDataCallback() has no payload — invocation
 * means the pay sheet finished (sample reloads the page).
 * MiniApp Checkout docs: resultCode === "1" means paid.
 */
export const isPaySuccess = (result) => {
  if (isPayCancelled(result)) return false;
  // "No permission" arrives as resultCode "1" — the success code — so this
  // check has to come first.
  if (bridgeFailureMessage(unwrapPayResult(result))) return false;
  if (result && typeof result === "object" && result.channel === "h5") {
    const code = payCode(result.result).toUpperCase();
    if (!code) return true;
    return ["1", "9000", "SUCCESS", "COMPLETED"].includes(code);
  }
  const code = payCode(unwrapPayResult(result)).toUpperCase();
  return ["1", "9000", "SUCCESS", "COMPLETED"].includes(code);
};
